/**
 * Шаг «голос клиента» (#843 вопросы клиентов, #844 итог разговора и причина
 * отказа) — схема хранимого результата `voice_extraction_v1` и сырого ответа
 * модели. Спека: docs/plans/2026-10-02-customer-questions-and-decline-reasons.md §3.
 *
 * `voice_extraction_v2` (#845, docs/plans/2026-10-02-conversation-themes.md §3) —
 * та же форма плюс темы разговора из набора организации: `theme_codes`, версии
 * их определений `theme_definition_versions` и `theme_status` (можно ли верить теме) и необязательный
 * `suggested_theme`. Пишется только у организации с открытыми
 * темами и хоть одной активной темой; остальные получают v1 как раньше, и
 * читатель таблицы принимает обе версии (`voiceExtractionResultSchema`).
 *
 * Отдельный шаг, а не поля основного анализа (решение владельца Р1): его
 * результат живёт в своей таблице `voice_extraction_results`, одна строка на
 * анализ.
 *
 * Наборы кодов — здесь, потому что их значения нужны схеме; ОПРЕДЕЛЕНИЯ кодов
 * и проверка версии — в `@aica/core/voc` (`voice-extraction-codes.ts`), как у
 * `objection_codes_v2` (#842): core берёт отсюда только типы. Правка определения
 * переносит ответы между кодами, поэтому поднимает версию набора — растяжка
 * держит тест там.
 */
import { z } from 'zod/v4';
import { RESERVED_THEME_CODE, normalizeThemeKey } from '@aica/core/voc/theme-key';
import { ORG_THEME_NAME_MAX, SINGLE_LINE_TEXT, orgThemeCodeSchema } from './org-themes';

/** Версия формы результата. Новая форма — новая версия, читатель сверяет её. */
export const VOICE_EXTRACTION_SCHEMA_VERSION = 'voice_extraction_v1';

/** Версия формы с темами разговора (#845): v1 + `theme_codes`, `theme_definition_versions`, `theme_status`, `suggested_theme`. */
export const VOICE_EXTRACTION_SCHEMA_VERSION_V2 = 'voice_extraction_v2';

/** Версия СМЫСЛА предметов вопросов (определения в `@aica/core/voc`). */
export const QUESTION_SUBJECT_CODES_VERSION = 'question_subject_v1';

/**
 * Предмет вопроса клиента — закрытый набор: группировка в «Голосе клиента» идёт
 * только по коду, формулировки — последние по дате (эмбеддингов и кластеров в
 * v1 нет, решение Р4). Порядок значим только для `other` — он последний.
 *
 * «Предмет», а не «тема»: темы разговора — набор организации (#845, v2 того же
 * шага), и два разных «theme» в одной схеме спутались бы.
 */
export const QUESTION_SUBJECT_CODES = [
  'price_and_inclusions',
  'payment_and_financing',
  'delivery_and_timing',
  'availability',
  'specs_and_configuration',
  'warranty_and_service',
  'documents',
  'comparison',
  'purchase_process',
  'other',
] as const;

export type QuestionSubjectCode = (typeof QUESTION_SUBJECT_CODES)[number];

/** Версия СМЫСЛА причин отказа (определения в `@aica/core/voc`). */
export const DECLINE_REASON_CODES_VERSION = 'decline_reason_codes_v1';

/**
 * Причины отказа: подмножество кодов возражений `objection_codes_v2` — тот же
 * словарь, чтобы «цена» в возражениях и в отказах была одним рядом (#847), — и
 * четыре кода, которых у возражений нет. Пересекающиеся по смыслу коды
 * возражений (`no_need`, `decision_authority`, `competitor_alternative`) не
 * взяты: у отказа их заменяют `no_current_need`, `not_decision_maker`,
 * `chose_competitor`, и два кода одного смысла в одном наборе разделили бы
 * один ряд надвое. Что подмножество — подмножество, держит тест в core.
 */
export const DECLINE_REASON_CODES = [
  'price',
  'budget',
  'financing',
  'payment_terms',
  'timing',
  'delivery_availability',
  'product_fit',
  'quality_reliability',
  'trust_service',
  'documents_contract',
  'unwanted_contact',
  'chose_competitor',
  'no_current_need',
  'not_decision_maker',
  'other',
] as const;

export type DeclineReasonCode = (typeof DECLINE_REASON_CODES)[number];

/** Итог разговора — только у категорий продаж (`isSalesCategory`). */
export const CONVERSATION_OUTCOMES = [
  'progressed',
  'postponed',
  'declined',
  'no_decision',
] as const;
export type ConversationOutcome = (typeof CONVERSATION_OUTCOMES)[number];

/** Ответил ли сотрудник на вопрос. */
export const QUESTION_ANSWERED_VALUES = ['full', 'partial', 'none'] as const;
export type QuestionAnswered = (typeof QUESTION_ANSWERED_VALUES)[number];

/** Где в разговоре прозвучал вопрос. */
export const QUESTION_PHASES = ['opening', 'middle', 'pre_decision'] as const;
export type QuestionPhase = (typeof QUESTION_PHASES)[number];

/** Сколько вопросов хранится у разговора. */
export const VOICE_EXTRACTION_MAX_QUESTIONS = 10;

/** Потолок длины формулировки вопроса и причины — одна короткая фраза. */
export const VOICE_EXTRACTION_PHRASE_MAX_CHARS = 120;

/**
 * Потолок длины цитаты. Цитата — ПДн и живёт дольше транскрипта; длиннее
 * одной реплики ей быть незачем (тот же порядок, что `MAX_TURN_CHARS` у
 * критериев).
 */
export const VOICE_EXTRACTION_QUOTE_MAX_CHARS = 300;

const questionSubjectCodeSchema = z.enum(QUESTION_SUBJECT_CODES);
const declineReasonCodeSchema = z.enum(DECLINE_REASON_CODES);
const conversationOutcomeSchema = z.enum(CONVERSATION_OUTCOMES);
const questionAnsweredSchema = z.enum(QUESTION_ANSWERED_VALUES);
const questionPhaseSchema = z.enum(QUESTION_PHASES);

/**
 * Секунда разговора, где прозвучала цитата. Есть только у звонков и берётся
 * НЕ у модели, а из диаризации по цитате (`null` — цитата не нашлась); у
 * чатов поля нет вовсе, как у `keyMoments`.
 */
const timestampSchema = z.number().int().min(0).nullable().optional();

/**
 * Порядковый номер сообщения чата (с нуля, в порядке сегментов диаризации
 * чата — по сообщению на сегмент) — двойник `timestamp` у чатов (#1578). Тоже
 * НЕ у модели: код находит цитату в собственном сообщении клиента тем же
 * заземлением, что даёт секунду звонку. `null` — цитата пустая или стоит не в
 * одном сообщении клиента. У звонков поля нет вовсе, как `timestamp` у чатов.
 *
 * Поле добавлено к форме v1 без новой версии: оно необязательно, строки до
 * него читаются как «номер не известен», а v2 занята темами (#845).
 */
const messageIndexSchema = z.number().int().min(0).nullable().optional();

export const customerQuestionSchema = z.object({
  /**
   * Суть вопроса одной фразой на языке анализа проекта.
   *
   * Пустая строка — формулировку целиком изъяла редакция (в ней было число,
   * похожее на номер договора или заказа, — `hasPotentialUnclassifiedIdentifier`).
   * Вопрос при этом был задан: он остаётся в списке и считается в частоту
   * своего `subject_code`. Читатель показывает такой вопрос как «формулировка
   * скрыта», а не пустой строкой и не выбрасывает его из счёта.
   */
  question: z.string().max(VOICE_EXTRACTION_PHRASE_MAX_CHARS),
  /**
   * Дословная цитата клиента — текст транскрипта, а не строка модели; пустая —
   * цитата не нашлась в его собственной реплике или изъята редакцией. Имя поля
   * — `quote`: его редактирует `minimizeDerivedOutput`.
   */
  quote: z.string().max(VOICE_EXTRACTION_QUOTE_MAX_CHARS),
  timestamp: timestampSchema,
  message_index: messageIndexSchema,
  subject_code: questionSubjectCodeSchema,
  answered: questionAnsweredSchema,
  phase: questionPhaseSchema,
});
export type CustomerQuestion = z.infer<typeof customerQuestionSchema>;

export const declineReasonSchema = z.object({
  code: declineReasonCodeSchema,
  /** Причина словами клиента, одна фраза на языке анализа проекта. */
  wording: z.string().max(VOICE_EXTRACTION_PHRASE_MAX_CHARS),
  quote: z.string().max(VOICE_EXTRACTION_QUOTE_MAX_CHARS),
  timestamp: timestampSchema,
  /** `true` — клиент назвал причину сам; `false` — причина выведена моделью. */
  stated: z.boolean(),
});
export type DeclineReason = z.infer<typeof declineReasonSchema>;

/**
 * Поля хранимого результата шага, общие для v1 и v2 — колонка
 * `voice_extraction_results.result`.
 *
 * `conversation_outcome: null` — разговор не из категории продаж: итог там не
 * спрашивается и в знаменатель отказов не входит. `decline_reason` есть только
 * при `declined` (и может отсутствовать и тогда — модель не нашла ни слов, ни
 * основания).
 */
const voiceExtractionResultShape = {
  question_subjects_version: z.literal(QUESTION_SUBJECT_CODES_VERSION),
  decline_reasons_version: z.literal(DECLINE_REASON_CODES_VERSION),
  customer_questions: z.array(customerQuestionSchema).max(VOICE_EXTRACTION_MAX_QUESTIONS),
  conversation_outcome: conversationOutcomeSchema.nullable(),
  decline_reason: declineReasonSchema.nullable(),
  /**
   * Только у чатов с сообщениями (диаризацией): номер первого сообщения
   * сотрудника в той же нумерации, что `message_index` вопроса; если его
   * первый ответ сегментом не стал (голый файл), — номер первого сегмента
   * после него; `null` — автор каждого сообщения известен, и сотрудник не
   * написал ни одного. Блок «вопросы первой минуты» берёт чат по правилу
   * «вопрос до первого сообщения сотрудника» (спека §3). Нет поля — чат до
   * #1578, без сообщений или с автором неизвестной роли до границы: первую
   * минуту по нему не измерить, он не входит ни в числитель, ни в
   * знаменатель блока.
   */
  first_agent_message_index: messageIndexSchema,
};

const declineOnlyWhenDeclined = [
  (value: { decline_reason: unknown; conversation_outcome: unknown }) =>
    value.decline_reason === null || value.conversation_outcome === 'declined',
  { message: 'decline_reason is only stored for a declined conversation' },
] as const;

export const voiceExtractionV1ResultSchema = z
  .object({
    schema_version: z.literal(VOICE_EXTRACTION_SCHEMA_VERSION),
    ...voiceExtractionResultShape,
  })
  .refine(...declineOnlyWhenDeclined);
export type VoiceExtractionV1Result = z.infer<typeof voiceExtractionV1ResultSchema>;

/** Сколько тем у разговора (spec §3): основная и до двух дополнительных. */
export const VOICE_EXTRACTION_MAX_THEMES = 3;

/**
 * Код «ни одна тема набора не подходит» — зарезервирован в наборе
 * (`RESERVED_THEME_CODE`), так что с кодом темы организации не совпадает.
 */
export const UNCATEGORIZED_THEME_CODE = RESERVED_THEME_CODE;

/** Потолок `suggested_theme` — потолок названия темы: предложение и есть название. */
export const VOICE_EXTRACTION_SUGGESTED_THEME_MAX_CHARS = ORG_THEME_NAME_MAX;

/**
 * Темы разговора: 1–3 кода набора организации, первый — основная, или ровно
 * `uncategorized`. Повторов нет; `uncategorized` рядом с настоящим кодом не
 * бывает. Что код — из набора ЭТОГО запроса, схема хранения не знает: это
 * держит нормализация шага (`normalizeThemeCodes`), отбрасывая чужие коды до
 * записи.
 */
export const conversationThemeCodesSchema = z
  .array(z.union([orgThemeCodeSchema, z.literal(UNCATEGORIZED_THEME_CODE)]))
  .min(1)
  .max(VOICE_EXTRACTION_MAX_THEMES)
  .refine((codes) => new Set(codes).size === codes.length, {
    message: 'theme codes are unique',
  })
  .refine((codes) => !codes.includes(UNCATEGORIZED_THEME_CODE) || codes.length === 1, {
    message: 'uncategorized is never stored next to a theme code',
  });

/**
 * Тема, которой в наборе нет, — название на языке анализа проекта: одна
 * строка (то же правило, что у названий тем набора), без пробелов по краям,
 * не пустая и с буквой или цифрой (то же правило, что у `orgThemeNameSchema`:
 * «—» не название) — пустое после минимизации ПДн не хранится вовсе (поля нет).
 */
export const suggestedThemeSchema = z
  .string()
  .min(1)
  .max(VOICE_EXTRACTION_SUGGESTED_THEME_MAX_CHARS)
  .regex(SINGLE_LINE_TEXT, 'must be a single line of text')
  .refine((value) => value.trim() === value, { message: 'must be trimmed' })
  .refine(
    (value) => normalizeThemeKey(value).length > 0,
    'must contain a letter or digit'
  );

/**
 * Можно ли верить теме строки (#845). `assigned` — модель назвала коды набора
 * этого запроса или честно сказала `uncategorized` («ни одна тема не
 * подходит»). `degraded` — тематическая часть ответа непригодна (не разобралась,
 * в ней только коды вне набора запроса, собранное не прошло схему), и
 * `theme_codes` стоит `["uncategorized"]` по умолчанию, а не по решению модели.
 *
 * Аналитика и недельная сводка ОБЯЗАНЫ исключать `degraded` — из числителя и
 * знаменателя доли «без темы» и из предложений тем: иначе сбой разбора
 * выглядит как «тема не нашлась» и кормит предложения. Поле обязательно у v2.
 * Строка v2, записанная до его появления (флаг был выключен у всех, но на
 * staging строки могли лечь), поля не несёт: читатель, идущий мимо этой схемы
 * (SQL по JSON), обязан считать отсутствие поля `degraded`, не `assigned`.
 */
export const VOICE_EXTRACTION_THEME_STATUSES = ['assigned', 'degraded'] as const;
export const voiceExtractionThemeStatusSchema = z.enum(VOICE_EXTRACTION_THEME_STATUSES);
export type VoiceExtractionThemeStatus = z.infer<typeof voiceExtractionThemeStatusSchema>;

/**
 * Версия определения каждой темы `theme_codes` — та, что ушла в промпт этого
 * разговора (`org_themes.definition_version` набора запроса), а не ответ
 * модели. Тот же порядок и та же длина, что у `theme_codes`; на месте
 * `uncategorized` — `null` (определения нет), у кода темы — целое ≥ 1.
 * Восстановить её позже нельзя: правка определения поднимает версию в наборе,
 * а сравнение периодов (спека §5) идёт по версии, с которой тема присвоена.
 */
export const themeDefinitionVersionsSchema = z.array(z.number().int().min(1).nullable());

/**
 * Ревизия набора, с которой работал шаг (#2012): последний момент журнала
 * набора (`OrgThemesRepository.activeSetWithRevision`, ISO UTC) из того же
 * снимка, что и сам набор. Тема набора видна разговору, только если ревизия не раньше её
 * базы (`since`): разговор, чей шаг прочёл набор до правки, а записался после,
 * не входит в строку темы ни числителем, ни знаменателем. Момент записи строки
 * (`created_at`, часы Postgres) этого не различает. Нет поля — строка
 * записана до #2012 (или журнал был пуст): читатель берёт `created_at`.
 */
export const themeSetRevisionSchema = z.string().datetime();

/**
 * Ревизия тематической части промпта, с которой работал шаг (#2952;
 * `VOICE_EXTRACTION_THEMES_PROMPT_REVISION` в workflows-api, например
 * `themes-v2`). Новая ревизия меняет распределение тем при тех же разговорах
 * (`themes-v1` → `themes-v2`: у большинства одна тема вместо двух–трёх),
 * поэтому блок тем сравнивает окна, только если оба прочитаны одной ревизией.
 * Нет поля — строка записана до #2952: её ревизия —
 * `THEMES_PROMPT_REVISION_BEFORE_FIELD`.
 */
export const themesPromptRevisionSchema = z.string().min(1).max(64);

/**
 * Ревизия промпта тем строки v2 без поля `themes_prompt_revision` (до #2952).
 * В прод шаг тем вышел сразу с `themes-v2`: и `themes-v1` (cc111b0f49), и
 * `themes-v2` (0022dde875) впервые ушли релизом v26.49.0 (деплой 2026-10-14
 * 22:20 UTC), первая строка v2 прода — 2026-10-15, и до #2952 ревизию никто
 * не менял. Поэтому строка без поля — `themes-v2`, а не «неизвестная»: иначе
 * после деплоя #2952 каждое окно, где есть старые строки, смешивало бы две
 * «ревизии», и сдвиг тем пропал бы у всех на две длины окна без смены промпта.
 * Строки `themes-v1` были только на staging (09–10.10).
 */
export const THEMES_PROMPT_REVISION_BEFORE_FIELD = 'themes-v2';

export const voiceExtractionV2ResultSchema = z
  .object({
    schema_version: z.literal(VOICE_EXTRACTION_SCHEMA_VERSION_V2),
    ...voiceExtractionResultShape,
    theme_codes: conversationThemeCodesSchema,
    theme_definition_versions: themeDefinitionVersionsSchema,
    theme_status: voiceExtractionThemeStatusSchema,
    suggested_theme: suggestedThemeSchema.optional(),
    theme_set_revision: themeSetRevisionSchema.optional(),
    themes_prompt_revision: themesPromptRevisionSchema.optional(),
  })
  .refine(...declineOnlyWhenDeclined)
  .refine(
    (value) =>
      value.theme_definition_versions.length === value.theme_codes.length &&
      value.theme_codes.every(
        (code, index) =>
          (code === UNCATEGORIZED_THEME_CODE) ===
          (value.theme_definition_versions[index] === null)
      ),
    {
      message:
        'theme_definition_versions follows theme_codes: one per code, null exactly at uncategorized',
      // The rule sits on the root; the path puts it on the theme part, so the
      // writer's culprit check (`THEME_PART_FIELDS`) reads it by path, not by text.
      path: ['theme_definition_versions'],
    }
  );
export type VoiceExtractionV2Result = z.infer<typeof voiceExtractionV2ResultSchema>;

/**
 * Хранимый результат любой версии — то, что принимает запись и что читатели
 * таблицы обязаны понимать. Версия различает форму (`schema_version`).
 */
export const voiceExtractionResultSchema = z.discriminatedUnion('schema_version', [
  voiceExtractionV1ResultSchema,
  voiceExtractionV2ResultSchema,
]);
export type VoiceExtractionResult = z.infer<typeof voiceExtractionResultSchema>;

/**
 * Сырой ответ модели — до нормализации. Строгая JSON-схема шага держит
 * перечни и обязательность полей; здесь — только ФОРМА, и мягко: длина
 * фразы, лишний вопрос сверх десяти или цитата длиннее потолка не должны
 * выбрасывать уже оплаченный ответ — их обрезает нормализация (тот же урок,
 * что у `RawScriptAdherenceEvaluationSchema`). Коды остаются строгими:
 * незнакомый код — не формулировка, которую можно подрезать.
 */
export const rawVoiceExtractionOutputSchema = z.object({
  customer_questions: z.array(
    z.object({
      question: z.string(),
      quote: z.string(),
      subject_code: questionSubjectCodeSchema,
      answered: questionAnsweredSchema,
      phase: questionPhaseSchema,
    })
  ),
  /** Нет у разговоров вне категорий продаж — схема для них поле не просит. */
  conversation_outcome: conversationOutcomeSchema.optional(),
  decline_reason: z
    .object({
      code: declineReasonCodeSchema,
      wording: z.string(),
      quote: z.string(),
      stated: z.boolean(),
    })
    .nullable()
    .optional(),
});
export type RawVoiceExtractionOutput = z.infer<typeof rawVoiceExtractionOutputSchema>;

/**
 * Тематическая часть сырого ответа v2 — разбирается ОТДЕЛЬНО от полей v1 и
 * мягко: коды — строки, а не перечень (код вне набора нормализация
 * отбрасывает), `suggested_theme` может отсутствовать или быть `null`. Сбой
 * разбора этой части не роняет оплаченный ответ: строка ложится с полями v1, темой
 * `uncategorized` и `theme_status: 'degraded'` (`toStoredVoiceExtractionV2`).
 */
export const rawVoiceExtractionThemesSchema = z.object({
  theme_codes: z.array(z.string()),
  suggested_theme: z.string().nullish(),
});
